import { SwipeCard } from './SwipeCard';
import { cn } from '@/lib/utils';
import { Sparkles, Lightbulb, TrendingUp, Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

export interface SuggestionData {
  id: string;
  type: 'suggestion' | 'alert' | 'insight' | 'nudge';
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  actionLabel?: string;
  actionPayload?: any;
}

interface SuggestionCardProps {
  suggestion: SuggestionData;
  onDismiss: (id: string) => void;
  onAct: (id: string, payload?: any) => void;
  onTap?: (id: string) => void;
  isTopCard?: boolean;
}

const typeConfig = {
  suggestion: {
    icon: Lightbulb,
    label: 'Suggestion',
    color: 'text-secondary',
    bg: 'bg-secondary/10',
  },
  alert: {
    icon: Bell,
    label: 'Alert',
    color: 'text-destructive',
    bg: 'bg-destructive/10',
  },
  insight: {
    icon: TrendingUp,
    label: 'Insight',
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
  nudge: {
    icon: Sparkles,
    label: 'Nudge',
    color: 'text-accent-foreground',
    bg: 'bg-accent/10',
  },
};

const priorityConfig = {
  low: { variant: 'secondary' as const, label: 'Low' },
  medium: { variant: 'default' as const, label: 'Medium' },
  high: { variant: 'destructive' as const, label: 'High' },
  critical: { variant: 'destructive' as const, label: 'Critical' },
};

export function SuggestionCard({
  suggestion,
  onDismiss,
  onAct,
  onTap,
  isTopCard = true,
}: SuggestionCardProps) {
  const config = typeConfig[suggestion.type];
  const priorityInfo = priorityConfig[suggestion.priority];
  const Icon = config.icon;

  return (
    <SwipeCard
      onSwipeLeft={() => onDismiss(suggestion.id)}
      onSwipeRight={() => onAct(suggestion.id, suggestion.actionPayload)}
      onTap={() => onTap?.(suggestion.id)}
      isTopCard={isTopCard}
      leftLabel="Dismiss"
      rightLabel={suggestion.actionLabel || 'Act'}
      leftColor="bg-muted"
      rightColor="bg-primary"
      className="w-full h-full"
    >
      <div className="p-6 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className={cn('flex items-center gap-2 px-3 py-1.5 rounded-full', config.bg)}>
            <Icon className={cn('h-4 w-4', config.color)} />
            <span className={cn('text-sm font-medium', config.color)}>
              {config.label}
            </span>
          </div>
          {suggestion.priority !== 'low' && (
            <Badge variant={priorityInfo.variant}>
              {priorityInfo.label}
            </Badge>
          )}
        </div>

        {/* Agent avatar */}
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {suggestion.title}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {suggestion.content}
            </p>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action hint */}
        <div className="flex items-center justify-center gap-6 pt-4 border-t border-border/50">
          <div className="flex items-center gap-2 text-muted-foreground">
            <span className="text-lg">←</span>
            <span className="text-sm">Dismiss</span>
          </div>
          <div className="w-px h-6 bg-border" />
          <div className="flex items-center gap-2 text-primary">
            <span className="text-sm font-medium">
              {suggestion.actionLabel || 'Take Action'}
            </span>
            <span className="text-lg">→</span>
          </div>
        </div>
      </div>
    </SwipeCard>
  );
}
